import { Typography, Box } from '@mui/material';
import React, { useContext, useState, useEffect } from 'react';
import Content from '../components/Layout/Content';
import ReusableTable from '../components/ReusableTable/ReusableTable';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import SearchBar from '../components/SearchBar/Search';
import PaginationComponent from '../components/paginationComponent/ServerSidePaginationComponent';
import {
  diagnosticsArrayConvertor,
  formatDateInMMDDYYYYWithComma,
  calculateDateFilterTimeStamp,
  getStatusFilterParamList,
  LAB_RESULT_STATUS_LIST,
} from '../shared/helper';
import {
  labResultsStatusFilterDatas,
  DEFAULT_NULL_REPLACEMENT,
  dateFilterMenuItems,
  NUMBER_OF_ROWS_PER_PAGE,
} from '../shared/constants';
import { ServiceFlyout } from '../components/ServiceFlyout/ServiceFlyout';
import { getIconFromSubItemsArray } from '../shared/helper';
import ApplicationContext from '../context/applicationContext';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import NotFound from '../components/NotFound/NotFoundPage';
import { useSnackbar } from 'notistack';
import { useLazyQuery } from '@apollo/client';
import {
  FETCH_ALL_DIAGNOSTIC_REPORTS_QUERY,
  FETCH_DIAGNOSTIC_REPORT_DETAILS_BY_ID,
} from '../services/LabResultsQueries/LabResultsService';
import {
  LAB,
  LAB_RESULT_TABLE_HEADING as tableHeading,
  LAB_RESULT_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';

const boxContainer = {
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  top: 40,
  flex: 1,
};

const table = {
  marginTop: 8,
  '& .css-1kn3lhz': {},
};

export const LabResults = props => {
  const [searchValue, setSearchValue] = useState('');
  const [recordsData, setRecordedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openFlyout, setOpenFlyout] = useState(false);
  const { menuItems } = useContext(ApplicationContext);
  const [detailsData, setDetailsData] = useState([]);
  const [totalCount, setTotalCount] = useState();
  const [offset, setOffset] = useState(0);
  const [modalTitle, setModalTitle] = useState('');
  const [selectedTableId, setSelectedTableId] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const closeFlyout = () => {
    setOpenFlyout(false);
  };

  const [fetchAllLabResults, fetchAllLabResultsCall] = useLazyQuery(
    FETCH_ALL_DIAGNOSTIC_REPORTS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(LAB.ON_ERROR_TEXT.WENT_WRONG_TEXT, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        let result = res.fetchAllDiagnosticReports.diagnosticList;
        let convetor = JSON.parse(result);
        let data = diagnosticsArrayConvertor(convetor);
        setRecordedData(data);
        setFilteredData(data);
        setTotalCount(res.fetchAllDiagnosticReports.count);
      },
    }
  );

  const [fetchDiagnosticReportById, fetchDiagnosticReportByIdCall] =
    useLazyQuery(FETCH_DIAGNOSTIC_REPORT_DETAILS_BY_ID, {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(LAB.ON_ERROR_TEXT.BY_ID, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        showFlyout(res.fetchDiagnosticReportById);
      },
    });

  const handleChangeFilter = () => {
    setOffset(0);
  };

  useEffect(() => {
    fetchAllLabResults({
      variables: {
        sourceUrl: '',
        count: NUMBER_OF_ROWS_PER_PAGE.toString(),
        offset: offset,
        dateFilter: calculateDateFilterTimeStamp(timeFilter),
        statusFilter: getStatusFilterParamList(
          statusFilter,
          labResultsStatusFilterDatas,
          LAB_RESULT_STATUS_LIST
        ),
        searchFilter: searchValue,
      },
    });
  }, [offset, searchValue, timeFilter, statusFilter]);

  const onPageClick = pageNumber => {
    setOffset((pageNumber - 1) * 10);
  };

  const onSearch = text => {
    setSearchValue(text);
  };

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      setTimeFilter(ev.target.value);
      handleChangeFilter();
      handleChangeFilter();
    },
  };

  const statusFilterOptions = {
    options: labResultsStatusFilterDatas,
    value: statusFilter,
    changeHandler: ev => {
      setStatusFilter(ev.target.value);
      handleChangeFilter();
      handleChangeFilter();
    },
  };

  const detailsLoading = fetchDiagnosticReportByIdCall.loading;

  const showFlyout = result => {
    const parseResult = JSON.parse(result?.diagnosticReport);
    const referenceRange =
      parseResult?.result?.length > 0
        ? JSON.parse(parseResult?.result?.[0]?.display)
        : '';
    const tableRowInfo = recordsData.find(obj => obj.id === selectedTableId);
    const modalData = [
      { column_name: LAB.MODAL_DATA.STATUS, value: tableRowInfo.status },
      {
        column_name: LAB.MODAL_DATA.RECORDED_ON,
        value: tableRowInfo.recordedOn,
      },
      {
        column_name: LAB.MODAL_DATA.RECORDED_BY,
        value: parseResult?.performer?.[0]?.display || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: LAB.MODAL_DATA.VALUE,
        value: referenceRange.value
          ? `${referenceRange.value} ${referenceRange.unit}`
          : DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: LAB.MODAL_DATA.REFERENCE_RANGE,
        value:
          referenceRange?.referenceRange
            ?.replaceAll('[', '')
            .replaceAll(']', '') || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: LAB.MODAL_DATA.FACILITY,
        value: parseResult?.encounter?.display || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: LAB.MODAL_DATA.TEST_RESULT,
        value:
          referenceRange?.interpretation
            ?.replaceAll('[', '')
            .replaceAll(']', '') || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: LAB.MODAL_DATA.ISSUED_DATE,
        value: parseResult.issued
          ? formatDateInMMDDYYYYWithComma(parseResult.issued)
          : DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: LAB.MODAL_DATA.ISSUER_OF_REPORT,
        value:
          parseResult?.resultsInterpreter?.[0]?.display ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: LAB.MODAL_DATA.CATEGORY_CODE,
        value: parseResult?.category?.[0]?.text || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: LAB.MODAL_DATA.LOINC_CODE,
        value: parseResult?.code?.coding
          ? `${parseResult?.code?.coding?.[0]?.code} ${parseResult?.code?.coding?.[0]?.display}`
          : DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: LAB.MODAL_DATA.NOTE,
        value: parseResult?.conclusion || DEFAULT_NULL_REPLACEMENT,
        width: 12,
      },
      {
        column_name: LAB.MODAL_DATA.DOCUMENTS,
        value:
          (result?.docArn?.length > 0 && result?.docArn[0]) ||
          DEFAULT_NULL_REPLACEMENT,
      },
    ];
    setDetailsData(modalData);
    setOpenFlyout(true);
  };

  const viewDetailsHandler = id => {
    setSelectedTableId(id);
    const tableRowInfo = recordsData.find(obj => obj.id === id);
    setModalTitle(tableRowInfo.name);
    fetchDiagnosticReportById({
      variables: {
        diagnosticReportId: id,
      },
    });
  };

  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="title" data-testid="heading">
        {LAB.TITLE_TEXT}
      </Typography>
      <Box sx={boxContainer}>
        <SearchBar
          sx={{
            width: '50%',
            minWidth: '250px',
            maxWidth: '500px',
            marginTop: '8px',
          }}
          onSearch={onSearch}
          placeholder={LAB.SEARCH_PLACEHOLDER_TEXT}
          setOffset={setOffset}
        />
        <FilterDropDown
          data-testid="filter-date"
          {...dateFilterOptions}
          filterType="Date:"
        />
        <FilterDropDown
          data-testid="filter-status"
          {...statusFilterOptions}
          filterType="Status:"
        />
      </Box>
      <Box sx={table}>
        {!fetchAllLabResultsCall.loading && !filteredData.length ? (
          <NotFound title={LAB.TITLE_TEXT} />
        ) : (
          <>
            <ReusableTable
              data-testid="reusabletable"
              tableHeading={tableHeading}
              data={filteredData}
              viewDetailsHandler={viewDetailsHandler}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginTop: 18,
              }}>
              {totalCount > NUMBER_OF_ROWS_PER_PAGE && (
                <PaginationComponent
                  numberOfRowsPerPage={NUMBER_OF_ROWS_PER_PAGE}
                  count={totalCount}
                  onPageClick={onPageClick}
                  page={offset / 10}
                />
              )}
            </div>
          </>
        )}
      </Box>
      <ServiceFlyout
        isOpen={openFlyout}
        handleClose={closeFlyout}
        data={detailsData}
        icon={
          menuItems
            ? getIconFromSubItemsArray(
                menuItems?.Medical?.subItems,
                'Lab Results'
              )
            : null
        }
        category={LAB.TITLE_TEXT}
        title={modalTitle}></ServiceFlyout>
      <SimpleBackdrop open={fetchAllLabResultsCall.loading || detailsLoading} />
    </Content>
  );
};
